/**
 * AgencyDashboard.jsx — Tableau de bord agence
 *
 * Accessible uniquement aux comptes avec role === 'agency'.
 * Sections disponibles :
 *  - overview      : synthèse de la flotte, graphique par type, messages récents
 *  - listings      : liste/grille de toutes les annonces (recherche, filtres)
 *  - form          : formulaire d'ajout ou de modification d'une annonce
 *                    (avec catalogue marques/modèles, upload photos, options)
 *  - availability  : gestion des périodes d'indisponibilité par véhicule
 *  - reservations  : consulter et confirmer/refuser les réservations
 *  - messages      : liste des conversations clients avec badge "non lu"
 *
 * Le catalogue de véhicules (/api/vehicle-catalog) remplit automatiquement
 * les champs marque → modèle → specs (places, carburant, transmission).
 * Les périodes d'indisponibilité sont aussi sauvegardées en localStorage
 * comme cache local en cas de perte réseau.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
	FiBarChart2,
	FiCalendar,
	FiCheckCircle,
	FiEdit3,
	FiFileText,
	FiHome,
	FiLogOut,
	FiMessageCircle,
	FiPlusCircle,
	FiRefreshCw,
	FiSearch,
	FiToggleLeft,
	FiToggleRight,
	FiTrash2,
	FiTrendingUp,
	FiTruck,
	FiXCircle
} from 'react-icons/fi';
import { notifyAuthUserChanged } from '../../utils/authEvents';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import CityCombobox from '../shared/CityCombobox.jsx';
import '../home/Home.css';
import './AgencyDashboard.css';

const apiBaseUrl = getApiBaseUrl();

// Navigation latérale : chaque entrée correspond à un panneau du dashboard
const sections = [
	{ id: 'overview', label: 'Synthèse', icon: FiBarChart2 },
	{ id: 'listings', label: 'Mes annonces', icon: FiTruck },
	{ id: 'form', label: 'Ajouter / Modifier', icon: FiPlusCircle },
	{ id: 'availability', label: 'Disponibilités', icon: FiCalendar },
	{ id: 'reservations', label: 'Réservations', icon: FiFileText },
	{ id: 'messages', label: 'Messages', icon: FiMessageCircle }
];

const DEFAULT_OPTIONS = [
	'Climatisation',
	'GPS',
	'Toit ouvrant',
	'Sièges chauffants',
	'Wi-Fi / Hotspot',
	'Siège bébé',
	'Galerie de toit',
	'Attelage / Remorque',
];

const defaultFormState = {
	name: '',
	vehicleBrand: '',
	vehicleModel: '',
	vehicleVersion: '',
	vehicleYear: '',
	vehicleType: 'Citadine',
	city: '',
	price: '',
	stock: '1',
	transmission: 'Automatique',
	fuelType: 'Essence',
	seats: '5',
	color: '',
	registrationNumber: '',
	description: '',
	selectedOptions: [],
	deliveryAvailable: false,
	deliveryPrice: '',
	driverAvailable: false,
	driverPrice: '',
	isAvailable: true
};

const defaultAvailabilityDraft = {
	listingId: '',
	startDate: '',
	endDate: '',
	reason: 'Location hors plateforme',
	note: ''
};

const VEHICLE_COLORS = [
	{ label: 'Blanc',       hex: '#FFFFFF' },
	{ label: 'Noir',        hex: '#1a1a1a' },
	{ label: 'Gris',        hex: '#9ca3af' },
	{ label: 'Argent',      hex: '#d1d5db' },
	{ label: 'Bleu',        hex: '#2563eb' },
	{ label: 'Bleu marine', hex: '#1e3a8a' },
	{ label: 'Rouge',       hex: '#ef4444' },
	{ label: 'Bordeaux',    hex: '#7f1d1d' },
	{ label: 'Vert',        hex: '#16a34a' },
	{ label: 'Beige',       hex: '#d9c9a0' },
	{ label: 'Marron',      hex: '#78350f' },
	{ label: 'Orange',      hex: '#f97316' },
	{ label: 'Jaune',       hex: '#fbbf24' },
];

const fuelTypes = ['Essence', 'Diesel', 'Hybride', 'Electrique'];
const transmissions = ['Automatique', 'Manuelle'];
const availabilityReasonOptions = [
	'Location hors plateforme',
	'Maintenance',
	'Entretien / nettoyage',
	'Réservation privée',
	'Fermeture saisonnière',
	'Autre'
];

/** Formate un prix en dirhams pour l'affichage (ex: "1 250 DH/jour") */
function formatCurrency(value) {
	return `${Number(value || 0).toLocaleString('fr-FR')} DH/jour`;
}

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = CURRENT_YEAR - 5;

function buildFeaturesList(selectedOptions) {
	return [...new Set(selectedOptions || [])];
}

/** Convertit un chemin d'image relatif en URL complète (laisse les URLs absolues intactes) */
function normalizeAssetUrl(path) {
	if (!path) return '';
	if (/^https?:\/\//i.test(path)) return path;
	if (path.startsWith('/')) return `${apiBaseUrl}${path}`;
	return `${apiBaseUrl}/${path}`;
}

function formatDateTime(value) {
	if (!value) return '-';
	return new Date(value).toLocaleString('fr-FR', {
		day: '2-digit', month: '2-digit', year: 'numeric',
		hour: '2-digit', minute: '2-digit'
	});
}

function formatDate(value) {
	if (!value) return '-';
	return new Date(value).toLocaleDateString('fr-FR', {
		day: '2-digit', month: '2-digit', year: 'numeric'
	});
}

/** Retourne la clé localStorage pour les périodes d'une annonce */
function getPeriodKey(listingId) {
	return `ux-agency-availability:${listingId}`;
}

/** Lit les périodes d'indisponibilité depuis le cache localStorage */
function readAvailabilityPeriods(listingId) {
	try {
		const raw = window.localStorage.getItem(getPeriodKey(listingId));
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

/** Écrit les périodes d'indisponibilité dans le cache localStorage */
function writeAvailabilityPeriods(listingId, periods) {
	window.localStorage.setItem(getPeriodKey(listingId), JSON.stringify(Array.isArray(periods) ? periods : []));
}

/**
 * normalizePeriods — Garantit un format uniforme pour toutes les périodes.
 * Génère un `id` unique si absent, et filtre les périodes sans dates.
 */
function normalizePeriods(periods) {
	if (!Array.isArray(periods)) return [];
	return periods
		.map((p) => ({
			id: p.id || p._id || `${p.startDate || ''}-${p.endDate || ''}-${Math.random().toString(36).slice(2, 8)}`,
			startDate: p.startDate || '',
			endDate: p.endDate || '',
			reason: p.reason || 'Location hors plateforme',
			note: p.note || '',
			createdAt: p.createdAt || new Date().toISOString()
		}))
		.filter((p) => p.startDate && p.endDate);
}

/** Retourne true si une période d'indisponibilité est active aujourd'hui */
function isPeriodActive(period) {
	const today = new Date();
	const start = new Date(`${period.startDate}T00:00:00`);
	const end = new Date(`${period.endDate}T23:59:59`);
	return today >= start && today <= end;
}

/** Retourne la classe CSS de couleur selon le statut d'une réservation */
function statusLabel(status) {
	const s = String(status || '').toLowerCase();
	if (s === 'confirmed' || s === 'approved' || s === 'resolved') return 'is-ok';
	if (s === 'pending' || s === 'open' || s === 'in_progress') return 'is-pending';
	return 'is-danger';
}

function AgencyDashboard() {
	const navigate = useNavigate();

	// ── Authentification ────────────────────────────────────────────────────
	const [authUser, setAuthUser] = useState(null);

	// ── États de chargement / feedback ──────────────────────────────────────
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');

	// ── Navigation interne ───────────────────────────────────────────────────
	const [activeSection, setActiveSection] = useState('overview');
	const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'

	// ── Données ──────────────────────────────────────────────────────────────
	const [listings, setListings] = useState([]);
	const [reservations, setReservations] = useState([]);
	const [discussions, setDiscussions] = useState([]);

	// ── Formulaire d'annonce ─────────────────────────────────────────────────
	const [formData, setFormData] = useState(defaultFormState);
	const [selectedFiles, setSelectedFiles] = useState([]);   // fichiers images sélectionnés
	const [previewUrls, setPreviewUrls] = useState([]);       // URL blob pour les aperçus
	const [editingId, setEditingId] = useState(null);         // null = création, sinon ID de l'annonce à modifier
	const [searchQuery, setSearchQuery] = useState('');

	// ── Formulaire d'indisponibilité ─────────────────────────────────────────
	const [availabilityDraft, setAvailabilityDraft] = useState(defaultAvailabilityDraft);

	// ── Catalogue véhicules ──────────────────────────────────────────────────
	const [catalog, setCatalog] = useState({ brands: [], models: [] });
	const [catalogLoading, setCatalogLoading] = useState(false);

	// Charger toutes les marques au montage
	useEffect(() => {
		fetch(`${apiBaseUrl}/api/vehicle-catalog/brands`)
			.then(r => r.json())
			.then(data => setCatalog(prev => ({ ...prev, brands: Array.isArray(data) ? data : [] })))
			.catch(() => {});
	}, []);

	// Charger les modèles quand la marque change
	useEffect(() => {
		if (!formData.vehicleBrand) return;
		setCatalog(prev => ({ ...prev, models: [] }));
		fetch(`${apiBaseUrl}/api/vehicle-catalog/models?brand=${encodeURIComponent(formData.vehicleBrand)}`)
			.then(r => r.json())
			.then(data => setCatalog(prev => ({ ...prev, models: Array.isArray(data) ? data : [] })))
			.catch(() => {});
	}, [formData.vehicleBrand]);

	/**
	 * applyModelSpecs — Appelle le catalogue pour remplir les specs automatiquement.
	 * Déclenché quand l'agence sélectionne un modèle : renseigne le type (Citadine/SUV…),
	 * le carburant, la transmission et le nombre de places.
	 */
	const applyModelSpecs = useCallback(async (brand, model) => {
		if (!brand || !model) return;
		setCatalogLoading(true);
		try {
			const res = await fetch(`${apiBaseUrl}/api/vehicle-catalog/specs?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}`);
			if (!res.ok) return;
			const entry = await res.json();
			if (entry?.defaultSpecs) {
				setFormData(prev => ({
					...prev,
					vehicleType:  entry.category || prev.vehicleType,
					seats:        String(entry.defaultSpecs.seats || prev.seats),
					fuelType:     entry.defaultSpecs.fuelType || prev.fuelType,
					transmission: entry.defaultSpecs.transmission || prev.transmission,
					description:  prev.description || entry.description || '',
					name:         prev.name || `${brand} ${model}`.trim(),
				}));
			}
		} catch { /* silently ignore */ } finally {
			setCatalogLoading(false);
		}
	}, []);
	// ────────────────────────────────────────────────────────────────────────

	useEffect(() => {
		const saved = window.localStorage.getItem('exp-auth-user');
		if (!saved) { navigate('/login'); return; }
		try {
			const parsed = JSON.parse(saved);
			if (parsed.role !== 'agency') { navigate('/'); return; }
			setAuthUser(parsed);
		} catch {
			navigate('/login');
		}
	}, [navigate]);

	useEffect(() => {
		return () => { previewUrls.forEach((url) => URL.revokeObjectURL(url)); };
	}, [previewUrls]);

	const resetForm = useCallback(() => {
		setFormData(defaultFormState);
		setEditingId(null);
		setSelectedFiles([]);
		setPreviewUrls((prev) => { prev.forEach((url) => URL.revokeObjectURL(url)); return []; });
	}, []);

	const syncListingAvailability = useCallback((listingId, periods) => {
		setListings((prev) =>
			prev.map((listing) => {
				const id = listing._id || listing.id;
				if (id !== listingId) return listing;
				return { ...listing, availabilityPeriods: normalizePeriods(periods) };
			})
		);
	}, []);

	const loadListings = useCallback(async () => {
		if (!authUser?.id) return;
		setLoading(true);
		setError('');
		try {
			const res = await fetch(`${apiBaseUrl}/api/products?agencyId=${encodeURIComponent(authUser.id)}`);
			const data = await res.json();
			if (!res.ok) throw new Error(data?.message || 'Impossible de charger les annonces.');
			const normalized = Array.isArray(data)
				? data.map((listing) => {
					const listingId = listing._id || listing.id;
					return {
						...listing,
						availabilityPeriods: normalizePeriods(
							listing.availabilityPeriods?.length
								? listing.availabilityPeriods
								: readAvailabilityPeriods(listingId)
						)
					};
				})
				: [];
			setListings(normalized);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, [authUser?.id]);

	const loadDiscussions = useCallback(async () => {
		if (!authUser?.id) return;
		try {
			const res = await fetch(`${apiBaseUrl}/api/discussions?agencyId=${encodeURIComponent(authUser.id)}`);
			const data = await res.json();
			if (!res.ok) throw new Error(data?.message || 'Impossible de charger les messages.');
			setDiscussions(Array.isArray(data) ? data : []);
		} catch (err) {
			setDiscussions([]);
			setError(err?.message || 'Impossible de charger les conversations.');
		}
	}, [authUser?.id]);

	const loadReservations = useCallback(async () => {
		if (!authUser?.id) return;
		try {
			const res = await fetch(`${apiBaseUrl}/api/reservations?agencyId=${encodeURIComponent(authUser.id)}`);
			const data = await res.json();
			if (!res.ok) return;
			setReservations(Array.isArray(data) ? data : []);
		} catch {
			setReservations([]);
		}
	}, [authUser?.id]);

	const loadData = useCallback(async () => {
		await Promise.all([loadListings(), loadDiscussions(), loadReservations()]);
	}, [loadListings, loadDiscussions, loadReservations]);

	useEffect(() => {
		if (!authUser?.id) return;
		loadData();
	}, [authUser?.id, loadData]);

	useEffect(() => {
		if (!listings.length || availabilityDraft.listingId) return;
		setAvailabilityDraft((prev) => ({ ...prev, listingId: listings[0]._id || listings[0].id }));
	}, [availabilityDraft.listingId, listings]);

	/**
	 * stats — Calcule les indicateurs de la flotte à partir des annonces.
	 * potentialMonthlyRevenue : prix × stock × 30 jours, uniquement les véhicules actifs.
	 * temporarilyBlocked : annonces ayant au moins une période d'indisponibilité active aujourd'hui.
	 */
	const stats = useMemo(() => {
		const total = listings.length;
		const active = listings.filter((l) => l.isAvailable !== false).length;
		const paused = total - active;
		const avgPrice = total ? Math.round(listings.reduce((s, l) => s + Number(l.price || 0), 0) / total) : 0;
		const potentialMonthlyRevenue = listings
			.filter((l) => l.isAvailable !== false)
			.reduce((s, l) => s + Number(l.price || 0) * Number(l.stock || 1) * 30, 0);
		const temporarilyBlocked = listings.filter((l) => (l.availabilityPeriods || []).some(isPeriodActive)).length;
		return { total, active, paused, avgPrice, potentialMonthlyRevenue, temporarilyBlocked };
	}, [listings]);

	const summaryCards = useMemo(() => [
		{ label: 'Annonces', value: stats.total, sub: `${stats.active} actives`, icon: FiTruck, color: 'blue' },
		{ label: 'Bloquées', value: stats.temporarilyBlocked, sub: 'périodes actives', icon: FiCalendar, color: 'orange' },
		{ label: 'Prix moyen', value: `${stats.avgPrice} DH`, sub: 'par jour', icon: FiBarChart2, color: 'purple' },
		{ label: 'CA potentiel', value: `${Math.round(stats.potentialMonthlyRevenue / 1000)}k DH`, sub: 'estimé / mois', icon: FiTrendingUp, color: 'green' },
		{ label: 'Réservations', value: reservations.length, sub: 'au total', icon: FiFileText, color: 'indigo' },
		{ label: 'Messages', value: discussions.length, sub: 'conversations', icon: FiMessageCircle, color: 'teal' }
	], [stats, reservations.length, discussions.length]);

	const recentDiscussions = useMemo(() => discussions.slice(0, 8), [discussions]);

	// Nombre de conversations avec un message non lu (dernier message envoyé par le client)
	const unreadDiscussionsCount = useMemo(() => {
		return discussions.filter((d) => {
			const msgs = d.messages || [];
			const last = msgs[msgs.length - 1];
			return last && last.senderRole !== 'agency' && last.senderRole !== 'system';
		}).length;
	}, [discussions]);

	const filteredListings = useMemo(() => {
		if (!searchQuery.trim()) return listings;
		const q = searchQuery.trim().toLowerCase();
		return listings.filter((l) => {
			const haystack = [l.name, l.vehicleBrand, l.vehicleModel, l.city, l.vehicleType, l.registrationNumber, ...(l.features || [])]
				.join(' ').toLowerCase();
			return haystack.includes(q);
		});
	}, [listings, searchQuery]);

	/**
	 * analyticsByType — Calcule la répartition de la flotte par type de véhicule.
	 * `percent` est relatif au type le plus représenté (max = 100%) pour la barre graphique.
	 */
	const analyticsByType = useMemo(() => {
		const grouped = filteredListings.reduce((acc, l) => {
			const key = l.vehicleType || 'Autre';
			acc[key] = (acc[key] || 0) + 1;
			return acc;
		}, {});
		const maxVal = Math.max(1, ...Object.values(grouped));
		return Object.entries(grouped)
			.map(([type, count]) => ({ type, count, percent: Math.round((count / maxVal) * 100) }))
			.sort((a, b) => b.count - a.count);
	}, [filteredListings]);

	const authDisplayName = useMemo(() => {
		if (!authUser) return '';
		return `${authUser.firstName || ''} ${authUser.lastName || ''}`.trim() ||
			authUser.agencyName || authUser.username || authUser.email || 'Agence';
	}, [authUser]);

	const authAvatarFallback = useMemo(() => {
		if (!authDisplayName) return 'AG';
		return authDisplayName.split(' ').filter(Boolean).slice(0, 2)
			.map((p) => p[0]?.toUpperCase() || '').join('').slice(0, 2) || 'AG';
	}, [authDisplayName]);

	const handleFieldChange = (event) => {
		const { name, type, value, checked } = event.target;
		setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
	};

	const handleFilesChange = (event) => {
		const files = Array.from(event.target.files || []);
		setSelectedFiles(files);
		// Libère les URLs blob précédentes avant d'en créer de nouvelles (évite les fuites mémoire)
		setPreviewUrls((prev) => {
			prev.forEach((url) => URL.revokeObjectURL(url));
			return files.map((f) => URL.createObjectURL(f));
		});
	};

	// Déplace la photo à `index` en première position (ce sera la photo d'affiche de l'annonce)
	const setCoverPhoto = (index) => {
		if (index === 0) return;
		setSelectedFiles((prev) => {
			const next = [...prev];
			const [picked] = next.splice(index, 1);
			next.unshift(picked);
			return next;
		});
		setPreviewUrls((prev) => {
			const next = [...prev];
			const [picked] = next.splice(index, 1);
			next.unshift(picked);
			return next;
		});
	};

	const startEdit = (listing) => {
		setActiveSection('form');
		setError('');
		setSuccessMessage('');
		setEditingId(listing._id || listing.id);
		setFormData({
			name: listing.name || '',
			vehicleBrand: listing.vehicleBrand || '',
			vehicleModel: listing.vehicleModel || '',
			vehicleYear: listing.vehicleYear || '',
			vehicleType: listing.vehicleType || 'Citadine',
			city: listing.city || '',
			price: listing.price || '',
			stock: listing.stock ?? '1',
			transmission: listing.transmission || 'Automatique',
			fuelType: listing.fuelType || 'Essence',
			seats: listing.seats || '5',
			color: listing.color || '',
			registrationNumber: listing.registrationNumber || '',
			description: listing.description || '',
			selectedOptions: (listing.features || []).filter((f) => DEFAULT_OPTIONS.includes(f)),
			deliveryAvailable: listing.deliveryAvailable === true,
			deliveryPrice: listing.deliveryPrice || '',
			driverAvailable: listing.driverAvailable === true,
			driverPrice: listing.driverPrice || '',
			isAvailable: listing.isAvailable !== false
		});
		setSelectedFiles([]);
		setPreviewUrls((prev) => { prev.forEach((url) => URL.revokeObjectURL(url)); return []; });
	};

	/**
	 * saveListing — Crée ou modifie une annonce.
	 * Utilise FormData (multipart/form-data) pour envoyer les photos en même temps que les champs.
	 * En création : POST /api/products  |  En modification : PATCH /api/products/:id
	 */
	const saveListing = async (event) => {
		event.preventDefault();
		if (!authUser?.id) return;
		setSaving(true);
		setError('');
		setSuccessMessage('');
		try {
			if (!formData.name.trim()) throw new Error('Le nom du véhicule est obligatoire.');
			if (Number(formData.price) <= 0) throw new Error('Le prix journalier doit être supérieur à 0.');
			// 4 photos minimum uniquement à la création (en modification les photos existantes restent)
			if (!editingId && selectedFiles.length < 4) throw new Error('Au moins 4 photos sont requises pour publier une annonce.');
			const yearVal = Number(formData.vehicleYear);
			if (!yearVal) throw new Error('L\'année du véhicule est obligatoire.');
			if (yearVal < MIN_YEAR || yearVal > CURRENT_YEAR) throw new Error(`La loi marocaine exige un véhicule de moins de 5 ans (${MIN_YEAR}–${CURRENT_YEAR}).`);

			const body = new FormData();
			body.append('agencyId', authUser.id);
			body.append('agencyName', authUser.agencyName || authUser.username || 'Agence');
			body.append('name', formData.name);
			body.append('vehicleBrand', formData.vehicleBrand);
			body.append('vehicleModel', formData.vehicleModel);
			body.append('vehicleYear', formData.vehicleYear);
			body.append('vehicleType', formData.vehicleType);
			body.append('city', formData.city);
			body.append('price', String(formData.price));
			body.append('stock', String(formData.stock || 1));
			body.append('transmission', formData.transmission);
			body.append('fuelType', formData.fuelType);
			body.append('seats', String(formData.seats || 5));
			body.append('color', formData.color);
			body.append('registrationNumber', formData.registrationNumber);
			body.append('description', formData.description);
			body.append('isAvailable', String(Boolean(formData.isAvailable)));
			body.append('deliveryAvailable', String(Boolean(formData.deliveryAvailable)));
			body.append('deliveryPrice', String(Number(formData.deliveryPrice) || 0));
			body.append('driverAvailable', String(Boolean(formData.driverAvailable)));
			body.append('driverPrice', String(Number(formData.driverPrice) || 0));
			body.append('features', JSON.stringify(buildFeaturesList(formData.selectedOptions)));
			selectedFiles.forEach((f) => body.append('images', f));

			const isEdit = Boolean(editingId);
			const res = await fetch(isEdit ? `${apiBaseUrl}/api/products/${editingId}` : `${apiBaseUrl}/api/products`, {
				method: isEdit ? 'PATCH' : 'POST',
				body
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data?.message || 'Enregistrement impossible.');

			setSuccessMessage(isEdit ? 'Annonce mise à jour.' : 'Annonce créée avec succès.');
			resetForm();
			setActiveSection('listings');
			await loadListings();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (listingId) => {
		if (!authUser?.id) return;
		if (!window.confirm('Supprimer cette annonce définitivement ?')) return;
		setSaving(true);
		setError('');
		setSuccessMessage('');
		try {
			const res = await fetch(`${apiBaseUrl}/api/products/${listingId}?agencyId=${encodeURIComponent(authUser.id)}`, { method: 'DELETE' });
			const data = await res.json();
			if (!res.ok) throw new Error(data?.message || 'Suppression impossible.');
			window.localStorage.removeItem(getPeriodKey(listingId));
			setSuccessMessage('Annonce supprimée.');
			if (editingId === listingId) resetForm();
			await loadListings();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	const handleToggleAvailability = async (listing) => {
		if (!authUser?.id) return;
		setSaving(true);
		setError('');
		setSuccessMessage('');
		try {
			const body = new FormData();
			body.append('agencyId', authUser.id);
			body.append('isAvailable', String(listing.isAvailable === false));
			const res = await fetch(`${apiBaseUrl}/api/products/${listing._id || listing.id}`, { method: 'PATCH', body });
			const data = await res.json();
			if (!res.ok) throw new Error(data?.message || 'Mise à jour impossible.');
			setSuccessMessage('Disponibilité mise à jour.');
			await loadListings();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	/**
	 * handleAddAvailabilityPeriod — Ajoute une période d'indisponibilité.
	 * Stratégie double : sauvegarde d'abord en localStorage (cache immédiat),
	 * puis synchronise avec l'API. Ainsi, même en cas d'échec réseau, la période
	 * reste visible localement jusqu'à la prochaine reconnexion.
	 */
	const handleAddAvailabilityPeriod = async (event) => {
		event.preventDefault();
		if (!availabilityDraft.listingId || !availabilityDraft.startDate || !availabilityDraft.endDate) {
			setError('Sélectionne une voiture et une période valide.');
			return;
		}
		if (new Date(`${availabilityDraft.endDate}T00:00:00`) < new Date(`${availabilityDraft.startDate}T00:00:00`)) {
			setError('La date de fin doit être après la date de début.');
			return;
		}
		const listing = listings.find((l) => (l._id || l.id) === availabilityDraft.listingId);
		if (!listing) { setError('Voiture introuvable.'); return; }

		setSaving(true);
		setError('');
		setSuccessMessage('');
		try {
			const listingId = listing._id || listing.id;
			const nextPeriod = {
				id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
				startDate: availabilityDraft.startDate,
				endDate: availabilityDraft.endDate,
				reason: availabilityDraft.reason,
				note: availabilityDraft.note,
				createdAt: new Date().toISOString()
			};
			const current = normalizePeriods(listing.availabilityPeriods);
			const updated = [...current, nextPeriod].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
			writeAvailabilityPeriods(listingId, updated);
			syncListingAvailability(listingId, updated);

			const body = new FormData();
			body.append('agencyId', authUser.id);
			body.append('availabilityPeriods', JSON.stringify(updated));
			const res = await fetch(`${apiBaseUrl}/api/products/${listingId}`, { method: 'PATCH', body });
			const data = await res.json();
			if (!res.ok) throw new Error(data?.message || "Impossible d'enregistrer la période.");

			setSuccessMessage("Période d'indisponibilité enregistrée.");
			setAvailabilityDraft((prev) => ({ ...prev, startDate: '', endDate: '', note: '' }));
			await loadListings();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	const handleRemoveAvailabilityPeriod = async (listingId, periodId) => {
		const listing = listings.find((l) => (l._id || l.id) === listingId);
		if (!listing) return;
		setSaving(true);
		setError('');
		setSuccessMessage('');
		try {
			const updated = normalizePeriods(listing.availabilityPeriods).filter((p) => p.id !== periodId);
			writeAvailabilityPeriods(listingId, updated);
			syncListingAvailability(listingId, updated);

			const body = new FormData();
			body.append('agencyId', authUser.id);
			body.append('availabilityPeriods', JSON.stringify(updated));
			const res = await fetch(`${apiBaseUrl}/api/products/${listingId}`, { method: 'PATCH', body });
			const data = await res.json();
			if (!res.ok) throw new Error(data?.message || 'Impossible de supprimer la période.');

			setSuccessMessage('Période supprimée.');
			await loadListings();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	const handleReservationStatus = async (reservation, newStatus) => {
		setSaving(true);
		setError('');
		setSuccessMessage('');
		try {
			const res = await fetch(`${apiBaseUrl}/api/reservations/${reservation._id || reservation.id}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ status: newStatus })
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data?.message || 'Mise à jour impossible.');
			setSuccessMessage('Réservation mise à jour.');
			await loadReservations();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	const openDiscussion = useCallback((discussion) => {
		const slug = encodeURIComponent((authUser?.agencyName || authUser?.username || authUser?.email || 'agency').replace(/\s+/g, '-'));
		navigate(`/chat/${slug}?mode=${discussion.contextType || 'agency'}`, {
			state: {
				listingId: discussion.listingId,
				vehicleName: discussion.vehicleName,
				agencyName: discussion.agencyName || authUser?.agencyName || authUser?.username || authUser?.email,
				contextType: discussion.contextType || 'agency',
				conversationKey: discussion.conversationKey
			}
		});
	}, [authUser?.agencyName, authUser?.email, authUser?.username, navigate]);

	const handleLogout = () => {
		window.localStorage.removeItem('exp-auth-user');
		window.localStorage.removeItem('exp-user-profile');
		notifyAuthUserChanged();
		navigate('/login');
	};

	const goToSection = (id) => {
		setActiveSection(id);
		setError('');
		setSuccessMessage('');
		if (id === 'messages') loadDiscussions();
	};

	if (!authUser) return null;

	return (
		<div className="ux-agency-page">
			{/* ── SIDEBAR ── */}
			<aside className="ux-agency-sidebar">
				<Link to="/" className="ux-agency-brand">
					<div className="ux-agency-brand-mark">
						<img src="/assets/images/logo.png" alt="UltimateXDrive" />
					</div>
					<div>
						<strong>UltimateX</strong>
						<strong>Agence</strong>
						<p>Espace Agence</p>
					</div>
				</Link>

				<nav className="ux-agency-nav" aria-label="Navigation agence">
					{sections.map((section) => {
						const SectionIcon = section.icon;
						return (
							<button
								key={section.id}
								type="button"
								className={`ux-agency-nav-item ${activeSection === section.id ? 'is-active' : ''}`}
								onClick={() => goToSection(section.id)}
							>
								<SectionIcon size={16} />
								<span className="ux-agency-nav-label">
									{section.id === 'form' && editingId ? 'Modifier annonce' : section.label}
								</span>
								{section.id === 'messages' && unreadDiscussionsCount > 0 ? (
									<span className="ux-agency-badge">{unreadDiscussionsCount}</span>
								) : null}
								{section.id === 'reservations' && reservations.length > 0 ? (
									<span className="ux-agency-badge">{reservations.length}</span>
								) : null}
							</button>
						);
					})}
				</nav>

				<div className="ux-agency-sidebar-footer">
					<div className="ux-agency-user-chip">
						<span className="ux-agency-user-avatar" aria-hidden="true">
							{authUser?.profilePhotoUrl ? (
								<img src={normalizeAssetUrl(authUser.profilePhotoUrl)} alt="" className="ux-agency-user-avatar-image" />
							) : (
								<span className="ux-agency-user-avatar-fallback">{authAvatarFallback}</span>
							)}
						</span>
						<div className="ux-agency-user-chip-info">
							<span>Connecté en tant que</span>
							<strong>{authDisplayName}</strong>
						</div>
					</div>
					<button type="button" className="ux-agency-logout" onClick={handleLogout}>
						<FiLogOut size={14} />
						Déconnexion
					</button>
				</div>
			</aside>

			{/* ── MAIN ── */}
			<main className="ux-agency-main">
				<header className="ux-agency-header">
					<div>
						<p className="ux-agency-kicker">Tableau de bord agence</p>
						<h1>{authUser?.agencyName || authUser?.username || 'Mon agence'}</h1>
						<p className="ux-agency-header-sub">Gérez votre flotte, vos disponibilités, vos réservations et vos clients</p>
					</div>
					<div className="ux-agency-header-actions">
						<Link to="/" className="ux-agency-btn ux-agency-btn-ghost">
							<FiHome size={14} />
							Accueil
						</Link>
						<button type="button" className="ux-agency-btn ux-agency-btn-primary" onClick={loadData} disabled={loading || saving}>
							<FiRefreshCw size={14} />
							Rafraîchir
						</button>
					</div>
				</header>

				{error ? <div className="ux-agency-alert">{error}</div> : null}
				{successMessage ? <div className="ux-agency-alert is-success">{successMessage}</div> : null}

				{/* ── SUMMARY ── */}
				<section className="ux-agency-summary-grid">
					{summaryCards.map((card) => {
						const CardIcon = card.icon;
						return (
							<article key={card.label} className={`ux-agency-summary-card ux-agency-summary-card--${card.color}`}>
								<div className="ux-agency-summary-card-header">
									<span>{card.label}</span>
									<div className={`ux-agency-summary-card-icon ux-agency-summary-card-icon--${card.color}`}>
										<CardIcon size={15} />
									</div>
								</div>
								<strong>{loading ? '···' : card.value}</strong>
								{card.sub ? <p>{card.sub}</p> : null}
							</article>
						);
					})}
				</section>

				{/* ── OVERVIEW ── */}
				{activeSection === 'overview' ? (
					<section className="ux-agency-panel">
						<h2>Synthèse de l'activité</h2>
						<div className="ux-agency-overview-grid">
							<div className="ux-agency-mini-card">
								<h3>Flotte active</h3>
								<p>{stats.active} véhicule(s) disponible(s) sur {stats.total}</p>
							</div>
							<div className="ux-agency-mini-card">
								<h3>Indisponibilités en cours</h3>
								<p>{stats.temporarilyBlocked} véhicule(s) bloqué(s) aujourd'hui</p>
							</div>
							<div className="ux-agency-mini-card">
								<h3>Réservations</h3>
								<p>{reservations.filter((r) => r.status === 'pending').length} en attente · {reservations.filter((r) => r.status === 'confirmed').length} confirmées</p>
							</div>
							<div className="ux-agency-mini-card">
								<h3>Conversations</h3>
								<p>{discussions.length} fil(s) client actif(s)</p>
							</div>
						</div>

						{analyticsByType.length > 0 ? (
							<div>
								<p className="ux-agency-analytics-title">Répartition par type de véhicule</p>
								<div className="ux-agency-analytics">
									{analyticsByType.map((item) => (
										<div key={item.type} className="ux-agency-analytics-row">
											<span className="ux-agency-analytics-label">{item.type}</span>
											<div className="ux-agency-analytics-bar-track">
												<div className="ux-agency-analytics-bar" style={{ width: `${item.percent}%` }} />
											</div>
											<span className="ux-agency-analytics-count">{item.count}</span>
										</div>
									))}
								</div>
							</div>
						) : null}

						<div className="ux-agency-quick-actions">
							<button type="button" className="ux-agency-btn ux-agency-btn-primary" onClick={() => { resetForm(); goToSection('form'); }}>
								<FiPlusCircle size={14} />
								Ajouter une annonce
							</button>
							<button type="button" className="ux-agency-btn ux-agency-btn-ghost" onClick={() => goToSection('availability')}>
								<FiCalendar size={14} />
								Gérer les indisponibilités
							</button>
							<button type="button" className="ux-agency-btn ux-agency-btn-ghost" onClick={() => goToSection('reservations')}>
								<FiFileText size={14} />
								Voir les réservations
							</button>
							<button type="button" className="ux-agency-btn ux-agency-btn-ghost" onClick={loadData} disabled={loading || saving}>
								<FiRefreshCw size={14} />
								Rafraîchir
							</button>
						</div>

						{recentDiscussions.length > 0 ? (
							<div>
								<p className="ux-agency-analytics-title">Messages récents</p>
								<div className="ux-agency-discussion-strip">
									{recentDiscussions.slice(0, 4).map((discussion) => (
										<article
											key={discussion._id || discussion.id}
											className="ux-agency-discussion-card"
											onClick={() => openDiscussion(discussion)}
											role="button"
											tabIndex={0}
											onKeyDown={(e) => e.key === 'Enter' && openDiscussion(discussion)}
										>
											<div className="ux-agency-discussion-head">
												<div>
													<h3>{discussion.vehicleName || 'Annonce'}</h3>
													<p>{discussion.customerName || 'Client'}</p>
												</div>
												<span className="ux-agency-discussion-date">
													{discussion.lastMessageAt ? formatDateTime(discussion.lastMessageAt) : '-'}
												</span>
											</div>
											<p>{discussion.lastMessagePreview || 'Aucun message récent.'}</p>
										</article>
									))}
								</div>
							</div>
						) : null}
					</section>
				) : null}

				{/* ── LISTINGS ── */}
				{activeSection === 'listings' ? (
					<section className="ux-agency-panel">
						<div className="ux-agency-panel-head">
							<h2>Mes annonces</h2>
							<div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
								<div className="ux-agency-search-bar">
									<FiSearch size={15} />
									<input
										type="text"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										placeholder="Rechercher par nom, ville, immat..."
									/>
								</div>
								<div className="ux-agency-view-switch">
									<button type="button" className={`ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm ${viewMode === 'grid' ? 'is-active' : ''}`} onClick={() => setViewMode('grid')} >Grille</button>
									<button type="button" className={`ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm ${viewMode === 'list' ? 'is-active' : ''}`} onClick={() => setViewMode('list')} >Liste</button>
								</div>
								<button type="button" className="ux-agency-btn ux-agency-btn-primary ux-agency-btn-sm" onClick={() => { resetForm(); goToSection('form'); }}>
									<FiPlusCircle size={13} />
									Ajouter
								</button>
							</div>
						</div>

						<span className="ux-agency-panel-count">{filteredListings.length} annonce(s)</span>

						{viewMode === 'grid' ? (
							<div className="ux-agency-listings-grid">
								{filteredListings.length ? filteredListings.map((listing) => {
									const listingId = listing._id || listing.id;
									const periods = normalizePeriods(listing.availabilityPeriods);
									const activeBlock = periods.find(isPeriodActive);
									const isUnavailable = activeBlock || listing.isAvailable === false;
									return (
										<article key={listingId} className="ux-agency-listing-card">
											<div className="ux-agency-card-img">
												{listing.mainImageUrl
													? <img src={normalizeAssetUrl(listing.mainImageUrl)} alt={listing.name} />
													: <div className="ux-agency-card-placeholder"><FiTruck size={28} /></div>}
												<div className={`ux-agency-status-badge ${isUnavailable ? 'is-paused' : 'is-active'}`}>
													<span className="ux-agency-status-dot" />
													{activeBlock ? 'Indisponible' : listing.isAvailable === false ? 'En pause' : 'Disponible'}
												</div>
											</div>
											<div className="ux-agency-card-body">
												<h3>{listing.name}</h3>
												<p className="ux-agency-card-type">{listing.vehicleType}</p>
												<p className="ux-agency-card-price">{formatCurrency(listing.price)}</p>
												<div className="ux-agency-card-tags">
													{listing.vehicleBrand ? <span>{listing.vehicleBrand} {listing.vehicleModel}</span> : null}
													{listing.city ? <span>{listing.city}</span> : null}
													{listing.transmission ? <span>{listing.transmission}</span> : null}
												</div>
												<div className="ux-agency-card-actions">
													<button type="button" className="ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm" onClick={() => startEdit(listing)}>
														<FiEdit3 size={13} /> Éditer
													</button>
													<button type="button" className="ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm" onClick={() => { goToSection('availability'); setAvailabilityDraft((prev) => ({ ...prev, listingId })); }}>
														<FiCalendar size={13} /> Dates
													</button>
													<button type="button" className="ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm" onClick={() => handleToggleAvailability(listing)} disabled={saving}>
														{listing.isAvailable === false ? <FiToggleLeft size={13} /> : <FiToggleRight size={13} />}
														{listing.isAvailable === false ? 'Activer' : 'Pause'}
													</button>
													<button type="button" className="ux-agency-btn ux-agency-btn-danger ux-agency-btn-sm" onClick={() => handleDelete(listingId)} disabled={saving}>
														<FiTrash2 size={13} />
													</button>
												</div>
											</div>
										</article>
									);
								}) : <div className="ux-agency-empty">Aucune annonce trouvée.</div>}
							</div>
						) : (
							<div className="ux-agency-table-wrap">
								<table className="ux-agency-table">
									<thead>
										<tr>
											<th>Véhicule</th>
											<th>Ville</th>
											<th>Prix / jour</th>
											<th>Statut</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody>
										{filteredListings.length ? filteredListings.map((listing) => {
											const listingId = listing._id || listing.id;
											const periods = normalizePeriods(listing.availabilityPeriods);
											const activeBlock = periods.find(isPeriodActive);
											return (
												<tr key={listingId}>
													<td>{listing.name}</td>
													<td>{listing.city || '-'}</td>
													<td>{formatCurrency(listing.price)}</td>
													<td>
																<span className={`ux-agency-status-badge ${activeBlock || listing.isAvailable === false ? 'is-paused' : 'is-active'}`} style={{position:'static',display:'inline-flex',alignItems:'center'}}>
																	<span className="ux-agency-status-dot" />
																	{activeBlock ? 'Indisponible' : listing.isAvailable === false ? 'Pause' : 'Disponible'}
																</span>
															</td>
													<td>
														<div className="ux-agency-table-row-actions">
															<button type="button" className="ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm" onClick={() => startEdit(listing)}><FiEdit3 size={13} /></button>
															<button type="button" className="ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm" onClick={() => { goToSection('availability'); setAvailabilityDraft((prev) => ({ ...prev, listingId })); }}><FiCalendar size={13} /></button>
															<button type="button" className="ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm" onClick={() => handleToggleAvailability(listing)} disabled={saving}>
																{listing.isAvailable === false ? <FiToggleLeft size={13} /> : <FiToggleRight size={13} />}
															</button>
															<button type="button" className="ux-agency-btn ux-agency-btn-danger ux-agency-btn-sm" onClick={() => handleDelete(listingId)} disabled={saving}><FiTrash2 size={13} /></button>
														</div>
													</td>
												</tr>
											);
										}) : (
											<tr><td colSpan={5}><div className="ux-agency-empty">Aucune annonce trouvée.</div></td></tr>
										)}
									</tbody>
								</table>
							</div>
						)}
					</section>
				) : null}

				{/* ── FORM ── */}
				{activeSection === 'form' ? (
					<section className="ux-agency-panel">
						<div className="ux-agency-panel-head">
							<h2>{editingId ? "Modifier l'annonce" : 'Nouvelle annonce'}</h2>
							{editingId ? <span className="ux-agency-panel-count">Modification en cours</span> : null}
						</div>
						<form className="ux-agency-form" onSubmit={saveListing}>
							<div className="ux-agency-form-row">
								{/* ── Marque ── */}
								<div className="ux-agency-form-field">
									<label>Marque *</label>
									<select name="vehicleBrand" value={formData.vehicleBrand} onChange={e => {
										setFormData(prev => ({ ...prev, vehicleBrand: e.target.value, vehicleModel: '', vehicleVersion: '' }));
										setCatalog(prev => ({ ...prev, models: [] }));
									}}>
										<option value="">— Sélectionnez une marque —</option>
										{catalog.brands.map(b => <option key={b} value={b}>{b}</option>)}
									</select>
								</div>
								{/* ── Modèle ── */}
								<div className="ux-agency-form-field">
									<label>Modèle *</label>
									<select name="vehicleModel" value={formData.vehicleModel} disabled={!formData.vehicleBrand || catalog.models.length === 0} onChange={e => {
										const model = e.target.value;
										setFormData(prev => ({ ...prev, vehicleModel: model, vehicleVersion: '' }));
										if (model) applyModelSpecs(formData.vehicleBrand, model);
									}}>
										<option value="">— Sélectionnez un modèle —</option>
										{catalog.models.map(m => <option key={m.model} value={m.model}>{m.model}</option>)}
									</select>
									{catalogLoading && <span style={{ fontSize: '0.75rem', color: '#2563eb', marginTop: 4 }}>Chargement des specs...</span>}
								</div>
							</div>
							<div className="ux-agency-form-row">
								{/* ── Version ── */}
								<div className="ux-agency-form-field">
									<label>Version / Finition</label>
									<select name="vehicleVersion" value={formData.vehicleVersion} disabled={!formData.vehicleModel} onChange={handleFieldChange}>
										<option value="">— Toutes versions —</option>
										{(catalog.models.find(m => m.model === formData.vehicleModel)?.versions || []).map(v => <option key={v} value={v}>{v}</option>)}
									</select>
								</div>
								<div className="ux-agency-form-field">
									<label>Année <span style={{fontSize:'0.68rem',color:'#e05c2a',fontWeight:600,textTransform:'none',letterSpacing:0}}>— moins de 5 ans (loi marocaine)</span></label>
									<input type="number" name="vehicleYear" value={formData.vehicleYear} onChange={handleFieldChange} placeholder={String(CURRENT_YEAR)} min={MIN_YEAR} max={CURRENT_YEAR} />
								</div>
							</div>
							<div className="ux-agency-form-row">
								<div className="ux-agency-form-field">
									<label>Nom de l'annonce *</label>
									<input name="name" value={formData.name} onChange={handleFieldChange} required placeholder="Ex: Renault Clio 5 Techno 2023" />
								</div>
							</div>
							<div className="ux-agency-form-row">
								<div className="ux-agency-form-field">
									<label>Prix / jour (DH) *</label>
									<input type="number" name="price" value={formData.price} onChange={handleFieldChange} min="1" required placeholder="250" />
								</div>
								<div className="ux-agency-form-field">
									<label>Stock</label>
									<input type="number" name="stock" value={formData.stock} onChange={handleFieldChange} min="1" placeholder="1" />
								</div>
							</div>
							<div className="ux-agency-form-row">
								<div className="ux-agency-form-field">
									<label>Transmission</label>
									<select name="transmission" value={formData.transmission} onChange={handleFieldChange}>
										{transmissions.map((t) => <option key={t} value={t}>{t}</option>)}
									</select>
								</div>
								<div className="ux-agency-form-field">
									<label>Carburant</label>
									<select name="fuelType" value={formData.fuelType} onChange={handleFieldChange}>
										{fuelTypes.map((f) => <option key={f} value={f}>{f}</option>)}
									</select>
								</div>
								<div className="ux-agency-form-field">
									<label>Sièges</label>
									<input type="number" name="seats" value={formData.seats} onChange={handleFieldChange} min="1" max="9" />
								</div>
							</div>
							<div className="ux-agency-form-row">
								<div className="ux-agency-form-field">
									<CityCombobox
										label="Ville"
										value={formData.city}
										onChange={city => setFormData(prev => ({ ...prev, city }))}
										placeholder="Rechercher une ville..."
									/>
								</div>
								<div className="ux-agency-form-field">
									<CityCombobox
										label="Ville d'immatriculation"
										value={formData.registrationNumber}
										onChange={city => setFormData(prev => ({ ...prev, registrationNumber: city }))}
										placeholder="Rechercher une ville..."
									/>
								</div>
								<div className="ux-agency-form-field">
									<label>Couleur{formData.color ? <span className="ux-agency-color-selected-label"> — {formData.color}</span> : ''}</label>
									<div className="ux-agency-color-swatches">
										{VEHICLE_COLORS.map(({ label, hex }) => (
											<button
												key={label}
												type="button"
												title={label}
												className={`ux-agency-color-swatch${formData.color === label ? ' is-active' : ''}`}
												style={{ background: hex, borderColor: hex === '#FFFFFF' ? '#d1d5db' : hex }}
												onClick={() => setFormData((prev) => ({ ...prev, color: prev.color === label ? '' : label }))}
											>
												{formData.color === label && (
													<span className="ux-agency-color-check" style={{ color: ['#FFFFFF','#d9c9a0','#fbbf24','#d1d5db'].includes(hex) ? '#374151' : '#fff' }}>✓</span>
												)}
											</button>
										))}
									</div>
								</div>
							</div>
							<div className="ux-agency-form-field">
								<label>Description</label>
								<textarea name="description" value={formData.description} onChange={handleFieldChange} rows="4" placeholder="Décrivez votre véhicule..." />
							</div>
							<div className="ux-agency-form-field">
								<label>Options du véhicule</label>
								<div className="ux-agency-options-tags">
									{DEFAULT_OPTIONS.map((option) => {
										const active = formData.selectedOptions.includes(option);
										return (
											<button
												key={option}
												type="button"
												className={`ux-agency-option-tag${active ? ' is-active' : ''}`}
												onClick={() => setFormData((prev) => ({
													...prev,
													selectedOptions: active
														? prev.selectedOptions.filter((o) => o !== option)
														: [...prev.selectedOptions, option]
												}))}
											>
												{option}
											</button>
										);
									})}
								</div>
							</div>
							<div className="ux-agency-form-field">
								<label>Services proposés</label>
								<div className="ux-agency-services-row">
									<div className="ux-agency-service-item">
										<button
											type="button"
											className={`ux-agency-option-tag${formData.deliveryAvailable ? ' is-active' : ''}`}
											onClick={() => setFormData((prev) => ({ ...prev, deliveryAvailable: !prev.deliveryAvailable }))}
										>
											Livraison
										</button>
										{formData.deliveryAvailable && (
											<div className="ux-agency-service-price">
												<input
													type="number"
													min="0"
													value={formData.deliveryPrice}
													onChange={(e) => setFormData((prev) => ({ ...prev, deliveryPrice: e.target.value }))}
													placeholder="Prix (DH)"
												/>
												<span>DH</span>
											</div>
										)}
									</div>
									<div className="ux-agency-service-item">
										<button
											type="button"
											className={`ux-agency-option-tag${formData.driverAvailable ? ' is-active' : ''}`}
											onClick={() => setFormData((prev) => ({ ...prev, driverAvailable: !prev.driverAvailable }))}
										>
											Chauffeur
										</button>
										{formData.driverAvailable && (
											<div className="ux-agency-service-price">
												<input
													type="number"
													min="0"
													value={formData.driverPrice}
													onChange={(e) => setFormData((prev) => ({ ...prev, driverPrice: e.target.value }))}
													placeholder="Prix (DH)"
												/>
												<span>DH / jour</span>
											</div>
										)}
									</div>
								</div>
							</div>
							<div className="ux-agency-form-field">
								<label>
									Photos
									<span className="ux-agency-photos-required"> — 4 minimum obligatoires</span>
								</label>
								<input type="file" accept="image/*" multiple onChange={handleFilesChange} />
								{previewUrls.length > 0 && (
									<>
										<p className="ux-agency-photos-count">
											{previewUrls.length} photo{previewUrls.length > 1 ? 's' : ''} sélectionnée{previewUrls.length > 1 ? 's' : ''}
											{previewUrls.length < 4 && (
												<span className="ux-agency-photos-warn"> — {4 - previewUrls.length} de plus requise{4 - previewUrls.length > 1 ? 's' : ''}</span>
											)}
										</p>
										<div className="ux-agency-preview-grid">
											{previewUrls.map((url, i) => (
												<div key={url + i} className={`ux-agency-preview-item${i === 0 ? ' is-cover' : ''}`}>
													<img src={url} alt={`Aperçu ${i + 1}`} />
													{i === 0 ? (
														<span className="ux-agency-cover-badge">Affiche</span>
													) : (
														<button
															type="button"
															className="ux-agency-set-cover-btn"
															onClick={() => setCoverPhoto(i)}
															title="Définir comme photo d'affiche"
														>
															Définir affiche
														</button>
													)}
												</div>
											))}
										</div>
									</>
								)}
							</div>
							<label className="ux-agency-form-checkbox">
								<input type="checkbox" name="isAvailable" checked={formData.isAvailable} onChange={handleFieldChange} />
								<span>Annonce visible et disponible à la location</span>
							</label>
							<div className="ux-agency-form-actions">
								<button type="submit" className="ux-agency-btn ux-agency-btn-primary" disabled={saving}>
									{saving ? 'Enregistrement...' : editingId ? 'Mettre à jour' : "Créer l'annonce"}
								</button>
								<button type="button" className="ux-agency-btn ux-agency-btn-ghost" onClick={() => { resetForm(); goToSection('listings'); }}>
									Annuler
								</button>
							</div>
						</form>
					</section>
				) : null}

				{/* ── AVAILABILITY ── */}
				{activeSection === 'availability' ? (
					<section className="ux-agency-panel">
						<div className="ux-agency-panel-head">
							<h2>Disponibilités — Périodes fermées</h2>
							<span className="ux-agency-panel-count">{stats.temporarilyBlocked} véhicule(s) bloqué(s) aujourd'hui</span>
						</div>
						<div className="ux-agency-availability-note">
							Bloquez un véhicule sur une période donnée : location hors plateforme, maintenance, fermeture temporaire... La voiture n'apparaîtra pas disponible durant ces dates.
						</div>

						{listings.length ? (
							<>
								<form className="ux-agency-form" onSubmit={handleAddAvailabilityPeriod}>
									<div className="ux-agency-form-row">
										<div className="ux-agency-form-field">
											<label>Véhicule</label>
											<select value={availabilityDraft.listingId} onChange={(e) => setAvailabilityDraft((prev) => ({ ...prev, listingId: e.target.value }))}>
												{listings.map((l) => (
													<option key={l._id || l.id} value={l._id || l.id}>{l.name}</option>
												))}
											</select>
										</div>
										<div className="ux-agency-form-field">
											<label>Date de début</label>
											<input type="date" value={availabilityDraft.startDate} onChange={(e) => setAvailabilityDraft((prev) => ({ ...prev, startDate: e.target.value }))} />
										</div>
										<div className="ux-agency-form-field">
											<label>Date de fin</label>
											<input type="date" value={availabilityDraft.endDate} onChange={(e) => setAvailabilityDraft((prev) => ({ ...prev, endDate: e.target.value }))} />
										</div>
									</div>
									<div className="ux-agency-form-row">
										<div className="ux-agency-form-field">
											<label>Raison</label>
											<select value={availabilityDraft.reason} onChange={(e) => setAvailabilityDraft((prev) => ({ ...prev, reason: e.target.value }))}>
												{availabilityReasonOptions.map((r) => <option key={r} value={r}>{r}</option>)}
											</select>
										</div>
										<div className="ux-agency-form-field">
											<label>Note (optionnelle)</label>
											<input type="text" value={availabilityDraft.note} onChange={(e) => setAvailabilityDraft((prev) => ({ ...prev, note: e.target.value }))} placeholder="Ex: client Alami, retour prévu le 20" />
										</div>
									</div>
									<div className="ux-agency-form-actions">
										<button type="submit" className="ux-agency-btn ux-agency-btn-primary" disabled={saving}>
											<FiCalendar size={14} />
											Bloquer la période
										</button>
									</div>
								</form>

								<div className="ux-agency-availability-list">
									{listings.map((listing) => {
										const listingId = listing._id || listing.id;
										const periods = normalizePeriods(listing.availabilityPeriods);
										return (
											<article key={listingId} className="ux-agency-availability-item">
												<div className="ux-agency-availability-item-head">
													<div>
														<h3>{listing.name}</h3>
														<p>{listing.vehicleBrand} {listing.vehicleModel} · {listing.city || 'Ville non définie'}</p>
													</div>
													<button type="button" className="ux-agency-btn ux-agency-btn-ghost ux-agency-btn-sm" onClick={() => setAvailabilityDraft((prev) => ({ ...prev, listingId }))}>
														Sélectionner
													</button>
												</div>
												{periods.length ? (
													<div className="ux-agency-periods-list">
														{periods.map((period) => {
															const active = isPeriodActive(period);
															return (
																<div key={period.id} className={`ux-agency-period-chip ${active ? 'is-current' : ''}`}>
																	<div>
																		<strong>{formatDate(period.startDate)} → {formatDate(period.endDate)}</strong>
																		<span>{period.reason}{period.note ? ` · ${period.note}` : ''}</span>
																	</div>
																	<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
																		{active ? <span className="ux-agency-period-active-tag">En cours</span> : null}
																		<button type="button" className="ux-agency-btn ux-agency-btn-danger ux-agency-btn-sm" onClick={() => handleRemoveAvailabilityPeriod(listingId, period.id)} disabled={saving}>
																			<FiTrash2 size={12} />
																		</button>
																	</div>
																</div>
															);
														})}
													</div>
												) : (
													<p className="ux-agency-empty-note">Aucune période bloquée pour ce véhicule.</p>
												)}
											</article>
										);
									})}
								</div>
							</>
						) : (
							<div className="ux-agency-empty">Ajoutez d'abord des annonces pour gérer leurs disponibilités.</div>
						)}
					</section>
				) : null}

				{/* ── RESERVATIONS ── */}
				{activeSection === 'reservations' ? (
					<section className="ux-agency-panel">
						<div className="ux-agency-panel-head">
							<h2>Réservations</h2>
							<span className="ux-agency-panel-count">{reservations.length} dossier(s)</span>
						</div>
						{reservations.length ? (
							<div className="ux-agency-reservations-stack">
								{reservations.map((reservation) => {
									const resId = reservation._id || reservation.id;
									return (
										<article key={resId} className="ux-agency-reservation-card">
											<div className="ux-agency-reservation-head">
												<div>
													<h3>{reservation.fullName || reservation.customerName || 'Client'}</h3>
													<p>{reservation.vehicleName || 'Véhicule'}</p>
													<p>{formatDate(reservation.startDate)} → {formatDate(reservation.endDate)}</p>
												</div>
												<span className={`ux-agency-status ${statusLabel(reservation.status)}`}>
													{reservation.status === 'pending' ? 'En attente' : reservation.status === 'confirmed' ? 'Confirmée' : reservation.status === 'cancelled' ? 'Annulée' : reservation.status}
												</span>
											</div>
											{reservation.email || reservation.phone ? (
												<p>Contact : {reservation.email || ''} {reservation.phone ? `· ${reservation.phone}` : ''}</p>
											) : null}
											{reservation.totalPrice ? (
												<p>Montant : <strong >{Number(reservation.totalPrice).toLocaleString('fr-FR')} DH</strong></p>
											) : null}
											{reservation.status === 'pending' ? (
												<div className="ux-agency-reservation-actions">
													<button type="button" className="ux-agency-btn ux-agency-btn-success ux-agency-btn-sm" onClick={() => handleReservationStatus(reservation, 'confirmed')} disabled={saving}>
														<FiCheckCircle size={13} />
														Confirmer
													</button>
													<button type="button" className="ux-agency-btn ux-agency-btn-danger ux-agency-btn-sm" onClick={() => handleReservationStatus(reservation, 'cancelled')} disabled={saving}>
														<FiXCircle size={13} />
														Refuser
													</button>
												</div>
											) : null}
										</article>
									);
								})}
							</div>
						) : (
							<div className="ux-agency-empty">Aucune réservation pour le moment.</div>
						)}
					</section>
				) : null}

				{/* ── MESSAGES ── */}
				{activeSection === 'messages' ? (
					<section className="ux-agency-panel">
						<div className="ux-agency-panel-head">
							<h2>Conversations clients</h2>
							<span className="ux-agency-panel-count">{discussions.length} conversation(s)</span>
						</div>
						{discussions.length ? (
							<div className="ux-agency-messages-list">
								{discussions.map((discussion) => {
									const msgs = discussion.messages || [];
									const lastMsg = msgs[msgs.length - 1];
									const isUnread = lastMsg && lastMsg.senderRole !== 'agency' && lastMsg.senderRole !== 'system';
									return (
										<article key={discussion._id || discussion.id} className={`ux-agency-message-row${isUnread ? ' is-unread' : ''}`}>
											<div className="ux-agency-message-icon">
												<FiMessageCircle size={18} />
												{isUnread && <span className="ux-agency-unread-dot" />}
											</div>
											<div className="ux-agency-message-body">
												<h3>
													{discussion.vehicleName || 'Annonce sans titre'}
													{isUnread && <span className="ux-agency-unread-label">Nouveau</span>}
												</h3>
												{discussion.listingId && (
													<Link
														to={`/vehicle/${discussion.listingId}`}
														className="ux-agency-listing-link"
														onClick={(e) => e.stopPropagation()}
													>
														Voir l'annonce
													</Link>
												)}
												<p>{discussion.lastMessagePreview || 'Aucun message récent.'}</p>
												<p className="ux-agency-message-meta">
													{discussion.customerName || 'Client'} · {formatDateTime(discussion.lastMessageAt)}
												</p>
											</div>
											<button type="button" className="ux-agency-btn ux-agency-btn-primary ux-agency-btn-sm" onClick={() => openDiscussion(discussion)}>
												Répondre
											</button>
										</article>
									);
								})}
							</div>
						) : (
							<div className="ux-agency-empty">Aucune conversation pour le moment.</div>
						)}
					</section>
				) : null}
			</main>
		</div>
	);
}

export default AgencyDashboard;
